import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { Status } from '@df/core/status';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { PageViewAbstract } from '../../../../catalog/ui/page/page-view.abstract';
import { StickyFreeComponent } from '../../../../common/sticky-free/sticky-free.component';
import { NCmsBlogGroup } from '../../group/cms-blog-group-navigation.interface';
import { CmsBlogGroupNavigationService } from '../../group/cms-blog-group-navigation.service';
import { CMS_BLOG_POST_CONFIG } from '../cms-blog-post.config';
import { NCmsBlogPost } from '../cms-blog-post.interface';

@Component({
    selector: 'cms-blog-post-view',
    templateUrl: 'cms-blog-post-view.component.html',
    styleUrl: './cms-blog-post-view.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        ElementHeightDirective,
        HrefComponent,
        ICmsClientModule,
        RouterModule,
        SizeClassDirective,
        StickyFreeComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsPageType(CMS_BLOG_POST_CONFIG)
export class CmsBlogPostViewComponent extends PageViewAbstract<NCmsBlogPost.ITypeData> implements OnChanges {
    /**
     * Blog tag categories
     */
    tagCategories: ICmsPage<IPageDataV2>[] = [];

    /**
     * Prev/next post container
     */
    nav?: NCmsBlogGroup.INavigation;

    /**
     * Navigation load status
     */
    navStatus = new Status();

    /**
     * Last 3 posts of the same parent
     */
    relatedArticles?: ICmsPage<IPageDataV2<NCmsBlogPost.ITypeData>>[];
    private lastShowRelated = false;

    constructor(protected cmsBlogGroupNavigationService: CmsBlogGroupNavigationService) {
        super();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['model']) {
            this.tagCategories = (this.model as ICmsPage<IPageDataV3>)?.['blogPostCategories']?.slice(0, 3);
            this.nav = undefined;

            this.cmsBlogGroupNavigationService.loadPageCreatedNav(this.navStatus, this.model, nav => {
                this.nav = nav;
                this.detectChanges();
            });
        }
    }

    get typeData() {
        return this.model?.data?.typeData;
    }

    get showRelated(): boolean {
        return this.model.showRelated;
    }

    /**
     * Fallback for old components data
     */
    getComponent(id: string) {
        return {
            id,
            ...this.typeData
        };
    }

    override detectChanges() {
        if (this.lastShowRelated !== this.showRelated) {
            this.lastShowRelated = this.showRelated;

            this.updateRelated();
        }

        super.detectChanges();
    }

    /**
     * Updates related articles
     */
    protected updateRelated() {
        if (this.showRelated) {
            this.model.loadRelatedArticles().then(relatedArticles => {
                this.relatedArticles = relatedArticles;
                this.detectChanges();
            });
        } else {
            this.relatedArticles = undefined;

            this.detectChanges();
        }
    }
}
